import { Fragment, useEffect, useState } from 'react';
import { StyleSheet, css } from 'aphrodite';

import { useRouter } from 'next/router';
import ReactPlaceholder from 'react-placeholder';

import BreadcrumbPlaceholder from '~/components/Placeholders/BreadcrumbPlaceholder';

import Link from 'next/link';
import { US_STATE_HASH_SHORT_TO_LONG } from '../utils/Constants';
import { capitalize } from '../utils/TextProcessing';
import { parseURLParam } from '../utils/ParseValues';
import { checkLocation } from '../utils/ErrorChecking';

const Breadcrumb = (props) => {
  const {
    passedCrumbs,
    passedPathname,
    passedAsPath,
    hero,
    marketReport,
    breadcrumbReady,
    isPrime,
    noCheck,
    agentSearchCrumb,
    hasNarrativeImage,
  } = props;
  const router = useRouter();

  const [asPath, setAsPath] = useState([]);
  const [pathname, setPathname] = useState([]);
  const [crumbs, setCrumbs] = useState([]);
  const [breadcrumbsDone, setBreadcrumbsDone] = useState(false);
  const [isValidLink, setIsValidLink] = useState([]);

  useEffect(() => {
    if (!passedCrumbs) {
      const asPathArray = router.asPath.split('/');
      const pathArray = router.pathname.split('/');
      const crumbArray = router.asPath.split('/');
      asPathArray.shift();
      pathArray.shift();
      crumbArray.shift();
      let currentAsPath = '';
      let currentPathname = '';
      for (let i = 0; i < crumbArray.length; i++) {
        let addCrumb = true;
        if (pathArray[i] === '[state_slug]') {
          crumbArray[i] = `${
            US_STATE_HASH_SHORT_TO_LONG[crumbArray[i].toUpperCase()]
          }`;
        } else if (pathArray[i] === '[city_slug]') {
          crumbArray[i] = `${capitalize(parseURLParam(crumbArray[i]))}`;
        } else if (pathArray[i] === '[agent_slug]') {
          const slugArray = crumbArray[i].split('-');
          slugArray.pop();

          crumbArray[i] = `${capitalize(slugArray.join(' '))}`;
        } else if (pathArray[i] === 'zip') {
          crumbArray.splice(i, 1);
          addCrumb = false;
        } else {
          crumbArray[i] = `${crumbArray[i]}`;
        }

        currentAsPath += `/${asPathArray[i]}`;
        currentPathname += `/${pathArray[i]}`;
        if (addCrumb) {
          pathArray[i] = currentPathname;
          asPathArray[i] = currentAsPath;
        }
      }
      checkValidLinks(asPathArray, crumbArray, pathArray);
      setCrumbs(crumbArray);
      setAsPath(asPathArray);
      setPathname(pathArray);
      setBreadcrumbsDone(true);
    } else {
      checkValidLinks(passedAsPath, passedCrumbs, passedPathname);
    }
  }, [router.asPath]);

  const checkValidLinks = async (asPathArray, crumbArray, pathArray) => {
    const isValid = [];
    for (let i = 0; i < asPathArray.length; i++) {
      const asPath = asPathArray[i];
      if (noCheck) {
        if (agentSearchCrumb) {
          isValid.push(true);
        } else if (i === crumbArray.length - 1 || !pathArray[i]) {
          isValid.push(false);
        } else {
          isValid.push(true);
        }
      } else if (pathArray[i] === '/[state_slug]') {
        isValid.push(true);
      } else if (
        asPath &&
        (asPath.includes('/state-directory') ||
          asPath.includes('/province-directory'))
      ) {
        isValid.push(true);
      } else if (asPath && pathArray[i] === '/[state_slug]/[city_slug]') {
        const segments = asPath.replace(/^\//, '').split('/');
        const state = segments[0];
        const city = segments[1];
        const slug = `${city}-${state}`;
        const locationResponse = await checkLocation({ city_slug: slug });
        if (
          locationResponse.statusCode >= 200 &&
          locationResponse.statusCode < 300
        ) {
          isValid.push(true);
        } else {
          isValid.push(false);
        }
      } else if (i === crumbArray.length - 1 || !pathArray[i]) {
        isValid.push(false);
      } else {
        isValid.push(false);
      }
    }
    setIsValidLink(isValid);
  };

  const renderBreadcrumb = () => {
    let crumbArray = [];
    let asPathArray = [];
    let pathArray = [];
    if (passedCrumbs) {
      crumbArray = passedCrumbs;
    } else {
      crumbArray = crumbs;
    }

    if (passedAsPath) {
      asPathArray = passedAsPath;
    } else {
      asPathArray = asPath;
    }

    if (passedPathname) {
      pathArray = passedPathname;
    } else {
      pathArray = pathname;
    }

    return crumbArray.map((crumb, index) => {
      return (
        <Fragment key={index}>
          {index > 0 && (
            <span
              className={css(
                styles.separator,
                hero && !marketReport && styles.white
              )}
            >
              <svg
                className="flex-shrink-0 h-5 w-5 text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  fillRule="evenodd"
                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </span>
          )}
          {isValidLink[index] ? (
            <Link
              prefetch={false}
              href={pathArray[index]}
              as={asPathArray[index]}
            >
              <span
                className={css(
                  styles.text,
                  hasNarrativeImage && styles.textWhite,
                  styles.link,
                  (hero && !marketReport) || (isPrime && styles.white)
                )}
              >
                {crumb}
              </span>
            </Link>
          ) : (
            <span
              className={css(
                styles.text,
                hasNarrativeImage && styles.textWhite,
                index === crumbArray.length - 1 && styles.activeCrumb,
                (hero && !marketReport) || (isPrime && styles.white)
              )}
            >
              {crumb}
            </span>
          )}
        </Fragment>
      );
    });
  };

  return (
    <ReactPlaceholder
      ready={breadcrumbReady || breadcrumbsDone}
      customPlaceholder={<BreadcrumbPlaceholder />}
      showLoadingAnimation
    >
      <div className={css(styles.breadcrumb)}>{renderBreadcrumb()}</div>
    </ReactPlaceholder>
  );
};

const styles = StyleSheet.create({
  link: {
    ':hover': {
      opacity: 0.7,
    },

    '@media only screen and (max-width: 767px)': {
      ':hover': {
        opacity: 1,
      },
    },
  },
  text: {
    textDecoration: 'none',
    color: 'rgba(36, 31, 58, 1)',
    opacity: 0.8,
    zIndex: 2,
  },
  textWhite: {
    color: '#fff !important',
  },
  breadcrumb: {
    fontSize: 16,
    display: 'flex',
    flexWrap: 'wrap',
    zIndex: 2,
    alignItems: 'center',

    '@media only screen and (max-width: 1023px)': {
      fontSize: 12,
    },
    '@media only screen and (max-width: 767px)': {
      alignItems: 'center',
    },
  },
  activeCrumb: {
    opacity: 1,
    fontWeight: 500,
  },
  white: {
    color: '#fff',
  },
  separator: {
    color: 'rgba(36, 31, 58, 1)',
    opacity: 0.8,
    margin: '0 20px',
    zIndex: 2,
    '@media only screen and (max-width: 767px)': {
      margin: '0 8px',
    },
  },
});

export default Breadcrumb;
